import type { ReactNode } from "react";

import { ErrorState, ExportTableDialog, HubTable, TableSettingsDialog } from "@/ds";
import type { AuditSortField } from "@/modules/audit";
import {
  hubTableCartao,
  HubTableCartao,
  HubTableDetailButton,
} from "@/shared/components/hub-table-cartao";
import { ListaToolbar } from "@/shared/components/lista-toolbar";
import { Tag } from "primereact/tag";
import { UserInfoCard } from "@/shared/components/user-info-card";
import { FiltrosInline } from "../components/filtros-inline";
import { AuditDetailDialog } from "./components/audit-detail-dialog";
import {
  AUDIT_ACTION_KEY,
  AUDIT_ACTIONS_HEADER,
  AUDIT_ACTIONS_KEY,
  AUDIT_ACTIONS_WIDTH,
  AUDIT_COLUMNS,
  AUDIT_ERROR_MESSAGE,
  AUDIT_EXPORT_LIST_NAME,
  AUDIT_LOADING_LABEL,
  AUDIT_MODULE_KEY,
  AUDIT_RESPONSAVEL_KEY,
  AUDIT_ROWS_PER_PAGE_LABEL,
  AUDIT_YEAR_KEY,
} from "./audit.constants";
import type { AuditColumnKey } from "./audit.constants";
import styles from "./audit.module.scss";
import type { AuditLinha, AuditViewModel } from "./types-audit";

/** Coluna Ação: só o nome; a alteração e os dados do evento ficam no modal de detalhe. */
function AcaoCell({ linha }: { linha: AuditLinha }) {
  return (
    <div className={styles.acao}>
      <span className={styles.acaoNome}>{linha.acao}</span>
    </div>
  );
}

/** Coluna Responsável: nome e detalhe; com pessoa por trás, o cartão de contato abre ao passar o cursor. */
function ResponsavelCell({ linha }: { linha: AuditLinha }) {
  const { responsavel } = linha;
  if (!responsavel) return null;

  const identificacao = (
    <span className={styles.responsavel}>
      <span className={styles.responsavelNome}>{responsavel.nome}</span>
      <span className={styles.responsavelDetalhe}>{responsavel.detalhe}</span>
    </span>
  );

  return responsavel.temCartao ? (
    <UserInfoCard
      nome={responsavel.nome}
      email={responsavel.cartao.email}
      telefone={responsavel.cartao.telefone}
      detalhe={responsavel.cartao.detalhe}
      testIds={responsavel.testIds}
    >
      {identificacao}
    </UserInfoCard>
  ) : (
    identificacao
  );
}

/** Aba Auditoria (Figma 4407:581544): Ano, Alteração, Módulo, Responsável e ⋮ sobre a tabela; a lupa ou a linha abre o detalhe. */
export function AuditView({
  isMobile,
  filterColumns,
  filterValue,
  aplicarFiltros,
  settings,
  exportView,
  notifySettingsApplied,
  items,
  total,
  isFirstLoad,
  status,
  errorMessage,
  reload,
  table,
  dataTableProps,
  hasFilters,
  vazio,
  limparFiltros,
  detalhando,
  detalhar,
  fecharDetalhe,
  reautenticar,
}: AuditViewModel) {
  function renderCell(key: AuditColumnKey, linha: AuditLinha): ReactNode {
    switch (key) {
      case "dataHora":
        return (
          <span className={styles.dataHora}>
            <span className={styles.data}>{linha.dataHora.data}</span>
            <span className={styles.hora}>{linha.dataHora.hora}</span>
          </span>
        );
      case "acao":
        return <AcaoCell linha={linha} />;
      case "justificativa":
        return <span className={styles.justificativa}>{linha.justificativa}</span>;
      case "modulo":
        return linha.modulo ? <Tag severity="secondary" value={linha.modulo} /> : null;
      case "responsavel":
        return <ResponsavelCell linha={linha} />;
      default:
        return null;
    }
  }

  return (
    <section className={styles.container} data-testid="audit-screen">
      <ListaToolbar
        testIdPrefix="audit"
        isMobile={isMobile}
        inlineActions
        quickFilters={
          <FiltrosInline
            definitions={filterColumns}
            value={filterValue}
            onChange={aplicarFiltros}
            onLimpar={limparFiltros}
            temFiltroAtivo={hasFilters}
            icones={{ [AUDIT_YEAR_KEY]: "calendar" }}
            obrigatorios={[AUDIT_YEAR_KEY]}
            testIdPrefix="audit"
            classNames={{
              [AUDIT_YEAR_KEY]: styles.filtroAno,
              [AUDIT_ACTION_KEY]: styles.filtroAlteracao,
              [AUDIT_MODULE_KEY]: styles.filtroModulo,
              [AUDIT_RESPONSAVEL_KEY]: styles.filtroResponsavel,
            }}
          />
        }
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {exportView.isOpen ? (
        <ExportTableDialog
          listName={AUDIT_EXPORT_LIST_NAME}
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog
        dialog={settings.dialog}
        testId="audit-table-settings"
        onApplied={notifySettingsApplied}
      />

      {/* A listagem permanece montada por trás do modal. */}
      {detalhando ? (
        <AuditDetailDialog
          recordId={detalhando.id}
          onClose={fecharDetalhe}
          onReauthenticate={reautenticar}
        />
      ) : null}

      <div className={styles.content}>
        <HubTableCartao<AuditLinha, AuditColumnKey, AuditSortField>
          value={items}
          totalRecords={total}
          table={table}
          settings={settings}
          status={status}
          errorMessage={errorMessage}
          loadingLabel={AUDIT_LOADING_LABEL}
          onRowClick={detalhar}
          emptyNoun={vazio.noun}
          emptyGender={vazio.gender}
          emptyPlural={vazio.plural}
          hasFilters={hasFilters}
          onClear={hasFilters ? limparFiltros : undefined}
          dataTableProps={dataTableProps}
          rowsPerPageLabel={AUDIT_ROWS_PER_PAGE_LABEL}
          testId="audit"
          data-testid="audit-table-wrapper"
          data-first-load={isFirstLoad || undefined}
        >
          {AUDIT_COLUMNS.map((coluna) => (
            <HubTable.Column<AuditLinha, AuditColumnKey>
              key={coluna.key}
              field={coluna.key}
              columnKey={coluna.key}
              header={coluna.header}
              style={coluna.width ? { minWidth: coluna.width } : undefined}
              body={(linha) => renderCell(coluna.key, linha)}
            />
          ))}
          <HubTable.Column<AuditLinha, AuditColumnKey>
            field={AUDIT_ACTIONS_KEY}
            columnKey={AUDIT_ACTIONS_KEY}
            header={AUDIT_ACTIONS_HEADER}
            sortable={false}
            align="center"
            alignHeader="center"
            bodyClassName={hubTableCartao.actionCellClassName}
            headerClassName={hubTableCartao.actionCellClassName}
            style={{ width: AUDIT_ACTIONS_WIDTH, minWidth: AUDIT_ACTIONS_WIDTH }}
            body={(linha) => (
              <HubTableDetailButton
                label={linha.detailLabel}
                testId={linha.detailTestId}
                onDetail={() => detalhar(linha)}
                opensDialog
              />
            )}
          />
          <HubTable.Error>
            <ErrorState
              message={AUDIT_ERROR_MESSAGE}
              description={errorMessage ?? undefined}
              onRetry={reload}
              testId="audit-error"
            />
          </HubTable.Error>
        </HubTableCartao>
      </div>
    </section>
  );
}
